import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch } from 'react-icons/fi';
import { Button, Checkbox, Flex, Modal, Stack, Text, Title } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fieldsQueryKeys } from 'util/queryKeys';
import {
  fieldQueryOptions,
  operationAgentsSamplersQueryOptions,
  operationQueryOptions,
  samplerOptions,
} from 'util/queryOptions';
import { getSamplerOptions, getScannerOptions } from 'util/samplePlan';
import { sortByValue } from 'util/sortByName';
import { userFullName } from 'util/userRoles';
import showToast from 'actions/toastActions';
import { DispatchType } from 'store/dispatch/types';
import { putSamplePlan } from 'store/samplePlans/requests';
import CenteredLoader from 'common/CenteredLoader';
import { Typeahead } from 'common/Components/Mantine/Typeahead';
import FieldInState from 'common/FieldInState';

type AssignSamplerPropsType = {
  dispatchPlan: DispatchType;
  onClose: VoidFunction;
  onSubmit: VoidFunction;
  opened: boolean;
};

export const AssignSampler = ({
  dispatchPlan,
  onClose,
  onSubmit,
  opened,
}: AssignSamplerPropsType) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [userSelected, setUserSelected] = useState<number | null>(null);
  const [scannerSelected, setScannerSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [assignToUnassignedPlans, toggleAssignToUnassigned] = useState(false);
  const [isSubmitting, toggleIsSubmitting] = useState(false);
  const [viewSampler, setViewSampler] = useState('');
  const [viewScanner, setViewScanner] = useState('');

  const { data: field } = useQuery(fieldQueryOptions(dispatchPlan.field_id));
  const { data: operation } = useQuery(operationQueryOptions(dispatchPlan.operation_id));
  const operationUsers =
    useQuery(operationAgentsSamplersQueryOptions(dispatchPlan.operation_id)).data?.users || [];

  const samplers = useQuery(samplerOptions()).data?.users || [];

  const isProOrTillRx = dispatchPlan.is_pro || dispatchPlan.is_till_rx;

  const onlySamplers = sortByValue(
    (samplers || []).map((user) => ({
      id: user.id,
      value: user.id,
      label: userFullName(user),
      can_scan: user.can_scan,
    })),
    'label',
  );

  const options: {
    id: number;
    value: number | string;
    label: string;
    can_scan: boolean;
  }[] = [
    {
      id: -1,
      value: '-1',
      label: 'None',
      can_scan: true,
    },
    ...getSamplerOptions(operation).filter((sampler) => sampler.id > 0),
    ...onlySamplers,
  ];

  const scannerOptions: {
    id: number;
    value: number | string;
    label: string;
  }[] = [
    {
      id: -1,
      value: '-1',
      label: 'None',
    },
    ...getScannerOptions(operation).filter((scanner) => scanner.id > 0),
    ...onlySamplers.filter((user) => user.can_scan),
  ];

  const submit = async () => {
    toggleIsSubmitting(true);
    const body = {
      assigned_to_id: userSelected && userSelected > 0 ? userSelected : null,
      assigned_to_scan_id:
        scannerSelected && scannerSelected > 0 && isProOrTillRx ? scannerSelected : null,
      field_id: dispatchPlan.field_id,
      assign_to_unassigned_plans: assignToUnassignedPlans,
    };
    try {
      await putSamplePlan(body, dispatchPlan.id);
    } catch (error) {
      showToast(t('assignSamplerError'), 'error', 7000);
    }
    toggleIsSubmitting(false);
    queryClient.invalidateQueries({
      queryKey: fieldsQueryKeys.field(dispatchPlan.field_id),
    });
    onSubmit();
    onClose();
  };

  useEffect(() => {
    if (options.length > 1 && loading) {
      if (dispatchPlan?.assigned_to_id && !userSelected) {
        const selected = options.find((option) => option.id === dispatchPlan?.assigned_to_id);
        if (selected && Number(selected?.value) > -1) {
          handleUserSelection(Number(selected.value), false);
          handleTextChangeUser(selected.label);
        }
        setLoading(false);
      }
      if (dispatchPlan?.assigned_to_scan_id && !scannerSelected) {
        const selected = scannerOptions.find(
          (option) => option.id === dispatchPlan?.assigned_to_scan_id,
        );
        if (selected && Number(selected?.value) > -1) {
          handleScannerSelection(Number(selected.value), false);
          handleTextChangeScanner(selected.label);
        }
        setLoading(false);
      }
      if (!dispatchPlan?.assigned_to_id && !dispatchPlan?.assigned_to_scan_id) {
        setLoading(false);
      }
    }
  }, [dispatchPlan, options, scannerOptions, userSelected, loading, scannerSelected]);

  const handleUserSelection = (val: number | string, setScanner = true) => {
    const sampler = options.find((option) => option.id === Number(val));
    setUserSelected(Number(val));
    setViewSampler(sampler?.label || '');
    if (sampler?.can_scan && setScanner && !scannerSelected && isProOrTillRx) {
      setScannerSelected(Number(val));
      setViewScanner(sampler?.label || '');
    }
  };

  const handleTextChangeUser = (value: string) => {
    setViewSampler(value);
  };

  const handleScannerSelection = (val: number | string, setSampler = true) => {
    const scanner = scannerOptions.find((option) => option.id === Number(val))?.label || '';
    setScannerSelected(Number(val));
    setViewScanner(scanner);
    if (setSampler && !userSelected) {
      setUserSelected(Number(val));
      setViewSampler(scanner);
    }
  };

  const handleTextChangeScanner = (value: string) => {
    setViewScanner(value);
  };

  const removeFilterSampler = () => {
    setUserSelected(null);
    setViewSampler('');
  };

  const removeFilterScanner = () => {
    setScannerSelected(null);
    setViewScanner('');
  };

  const getAssignSamplerBody = () => {
    if (!field || !operation || !operationUsers || isSubmitting || loading) {
      return <CenteredLoader h="25rem" />;
    }
    if (!operation.billing_user_id) {
      return <Text>{`*${t('assignSamplerNoBilling')}`}</Text>;
    }

    return (
      <>
        <Text>{t('assignSamplerPar')}:</Text>
        <Flex justify="space-between" w="80%">
          <Text>{t('soilCollection')}</Text>
          <Typeahead
            onTextChange={handleTextChangeUser}
            onSelect={handleUserSelection}
            onDeselect={removeFilterSampler}
            data={options}
            placeholder={t('searchSampler')}
            leftSection={<FiSearch />}
            value={viewSampler}
            w="20rem"
          />
        </Flex>
        {isProOrTillRx && (
          <Flex justify="space-between" w="80%">
            <Text>{t('soilScan')}</Text>
            <Typeahead
              onTextChange={handleTextChangeScanner}
              onSelect={handleScannerSelection}
              onDeselect={removeFilterScanner}
              data={scannerOptions}
              placeholder={t('searchSampler')}
              leftSection={<FiSearch />}
              value={viewScanner}
              w="20rem"
            />
          </Flex>
        )}
        <Checkbox
          checked={assignToUnassignedPlans}
          onChange={() => toggleAssignToUnassigned(!assignToUnassignedPlans)}
          label={t('assignAllPlans')}
        />
        <Button onClick={submit}>{t('inviteSampler')}</Button>
      </>
    );
  };

  return (
    <Modal opened={opened} onClose={onClose} size="lg" centered>
      <Stack justify="center" align="center" mb="lg">
        <Title order={3} fw={700}>
          {t('assignSampler')}
        </Title>
        {field && <FieldInState field={field} />}
        {getAssignSamplerBody()}
      </Stack>
    </Modal>
  );
};
